/* eslint-disable camelcase */
import { useEffect, useRef, useState } from "react";
import * as yup from "yup";
import i18n from "i18next";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Field, Form, Formik, FormikProps } from "formik";
import { enqueueSnackbar } from "notistack";
import { Grid, Button, Box } from "@material-ui/core";
import { useDynamicYupValidations } from "../../../../../libs/yup-validations";
import { OrganizationMembershipUserData } from "../../../../../models/organizationMembershipData";
import { createUserMembership } from "../../../../../redux/organizationMembership/action";
import CustomTextField from "../../../../forms/CustomTextField";
import CompanyHeading from "../../../../theming/CompanyHeading";
import CustomDatepicker from "../../../../forms/CustomDatepicker";
import { formatDateToBirthday } from "../../profileSettingsUtils";
import { AppState } from "../../../../../redux";
import { userNeedsAGuardian } from "../../../../../utils/userNeedsAGuardian";
import { fetchMinimumAge } from "../../../../../redux/system-settings/actions";
import CustomCheckbox from "../../../../forms/CustomCheckbox";

/**
 * @interface MembershipAddFormData
 */
interface MembershipAddFormData {
  firstName: string;
  lastName: string;
  email: string;
  username: string;
  birthday: Date | null;
  street: string;
  street_number: string;
  zip_code: string;
  city: string;
  country: string;
}

/**
 * @interface AddMembershipProps
 */
interface AddMembershipProps {
  organizationId: string;
  onMembershipAdded: () => void;
  setIsOpenSection: React.Dispatch<React.SetStateAction<boolean>>;
  skipGuardians: boolean;
  addUserWithoutMail: boolean;
}

/**
 * Component for adding single memberships to an organization.
 * @param {AddMembershipProps} props - The component props.
 */
const AddMembership = ({
  organizationId,
  onMembershipAdded,
  setIsOpenSection,
  skipGuardians,
  addUserWithoutMail,
}: AddMembershipProps) => {
  const { t } = useTranslation(["common", "organization"]);
  const addressRequired = useSelector(
    (state: AppState) => state.metaData.metaData.address_required ?? true
  );
  const [minimumAge, setMinimumAge] = useState<number>(0);

  const minimumAgeFetched = useSelector((state: AppState) => state.minimumAge);

  // eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
  const [addedMembers, setAddedMembers] = useState<OrganizationMembershipUserData[]>([]);

  const formikRef = useRef<FormikProps<MembershipAddFormData> | null>(null);

  // TODO CHECK WHY THIS NEEDED?
  /**
   * Fetches the minimum age from the API.
   * The minimum age is used to determine if a user needs a guardian.
   */
  useEffect(() => {
    dispatch(fetchMinimumAge());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sets the minimum age from the API response to the state.
   */
  useEffect(() => {
    if (!minimumAgeFetched?.isLoading && minimumAgeFetched?.minimumAge?.minimum_age) {
      setMinimumAge(minimumAgeFetched.minimumAge.minimum_age);
    }
  }, [minimumAgeFetched]);

  useEffect(() => {
    if (addUserWithoutMail && formikRef.current) {
      formikRef.current.setFieldValue("email", "");
      formikRef.current.setFieldTouched("email", false);
      formikRef.current.setFieldError("email", undefined);
    }
  }, [addUserWithoutMail]);

  const dispatch = useDispatch();
  const {
    YupValidationFirstName,
    YupValidationLastName,
    YupValidationEmail,
    YupValidationStreet,
    YupValidationStreetNumber,
    YupValidationCity,
    YupValidationZipCode,
    YupValidationCountry,
    YupValidationBirthday,
  } = useDynamicYupValidations(addressRequired);

  const validationSchema = yup.object({
    firstName: YupValidationFirstName,
    lastName: YupValidationLastName,
    email: addUserWithoutMail ? yup.string().nullable().strip() : YupValidationEmail,
    birthday: YupValidationBirthday,
    street: YupValidationStreet,
    street_number: YupValidationStreetNumber,
    zip_code: YupValidationZipCode,
    city: YupValidationCity,
    country: YupValidationCountry,
    guardianConsent: yup.boolean().when("birthday", (birthday, schema) => {
      if (
        birthday &&
        userNeedsAGuardian(formatDateToBirthday(birthday), minimumAge, skipGuardians)
      ) {
        return schema
          .oneOf([true], t("guardianConsentRequired", { ns: "validations" }))
          .required();
      }
      return schema.notRequired().default(true);
    }),
  });

  /**
   * Handles form submission.
   * @param {MembershipAddFormData} values - The form values.
   */
  const handleSubmit = async (
    values: MembershipAddFormData,
    { resetForm }: { resetForm: () => void }
  ) => {
    try {
      const data: OrganizationMembershipUserData = {
        first_name: values.firstName,
        last_name: values.lastName,
        username: values.username,
        email: values.email,
        birthday: formatDateToBirthday(values.birthday),
        street: values?.street,
        street_number: values?.street_number,
        zip_code: values?.zip_code,
        city: values?.city,
        country: values?.country,
        organization_id: organizationId,
      };
      const success = await dispatch(createUserMembership(data));
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (success) {
        setAddedMembers((prevAddedMembers) => [...prevAddedMembers, data]);
        resetForm();
        onMembershipAdded();
        setIsOpenSection(false);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    }
  };

  return (
    <Box style={{ marginTop: "3rem", paddingBottom: "3rem" }}>
      <CompanyHeading>
        {t("membership.addMember", { ns: "organization" })}
      </CompanyHeading>
      <Formik
        key={i18n.language}
        innerRef={formikRef}
        enableReinitialize
        validationSchema={validationSchema}
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          username: "",
          birthday: null,
          street: "",
          street_number: "",
          zip_code: "",
          city: "",
          country: "Deutschland",
          guardianConsent: false,
        }}
        onSubmit={handleSubmit}
      >
        {({ values }) => (
          <Form>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  autoComplete="firstName"
                  name="firstName"
                  id="firstname-textfield-add-membership"
                  label={t("profile.firstName", { ns: "common" })}
                  value={values.firstName ?? ""}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CustomTextField
                  fullWidth
                  value={values.lastName ?? ""}
                  autoComplete="lastName"
                  name="lastName"
                  id="lastname-textfield-add-membership"
                  label={t("profile.lastName", { ns: "common" })}
                  required
                />
              </Grid>
              {!addUserWithoutMail ? (
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    value={values.email ?? ""}
                    type="email"
                    autoComplete="email"
                    name="email"
                    id="email-textfield-add-membership"
                    label={t("profile.mail", { ns: "common" })}
                    required={!addUserWithoutMail}
                  />
                </Grid>
              ) : null}
              {addUserWithoutMail ? (
                <Grid item xs={12} sm={6}>
                  <CustomTextField
                    fullWidth
                    value={values.username ?? ""}
                    type="username"
                    autoComplete="username"
                    name="username"
                    id="username-textfield-add-membership"
                    label={t("profile.usernameExisting", { ns: "common" })}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} sm={6} md={6}>
                <Field
                  as={CustomDatepicker}
                  fullWidth
                  name="birthday"
                  label={t("profile.birthdate", { ns: "common" })}
                  value={values.birthday ?? ""}
                  required
                />
              </Grid>
              {addressRequired ? (
                <>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="street"
                      label={t("profile.street", { ns: "common" })}
                      value={values.street ?? ""}
                      required={addressRequired}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="street_number"
                      label={t("profile.streetNumber", { ns: "common" })}
                      value={values.street_number ?? ""}
                      required={addressRequired}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="zip_code"
                      label={t("profile.zipCode", { ns: "common" })}
                      value={values.zip_code ?? ""}
                      required={addressRequired}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="city"
                      label={t("profile.city", { ns: "common" })}
                      value={values.city ?? ""}
                      required={addressRequired}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <CustomTextField
                      fullWidth
                      name="country"
                      label={t("profile.country", { ns: "common" })}
                      value={values.country ?? ""}
                      required={addressRequired}
                    />
                  </Grid>
                </>
              ) : null}
              {values.birthday &&
              userNeedsAGuardian(
                formatDateToBirthday(values.birthday),
                minimumAge,
                skipGuardians
              ) ? (
                <Grid item xs={12}>
                  <CustomCheckbox
                    name="guardianConsent"
                    label={t("skipGuardian", { ns: "organization" })}
                  />
                </Grid>
              ) : null}
              <Grid item xs={12} style={{ marginTop: "1.25rem" }}>
                <Button
                  id="add-organization"
                  type="submit"
                  size="large"
                  variant="contained"
                  color="primary"
                >
                  {t("membership.add", { ns: "organization" })}
                </Button>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
export default AddMembership;
